import { Dispatch, useMemo, useState } from 'react'
import { Separator, Card, Text, Image, Checkbox, Icon } from '@/ui'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { MissingStatementCard } from './missingBankMonthUpload'
import { MissingStatementMultipleUploadCard } from './missingBankMonthMultipleUpload'
import {
  MissingStatement,
  UserDocument,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { InstitutionLogo } from '@/features/user/settings/sections/connected-financial-institutions-card/institution-card/logo'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { TypographyStyles } from '@/styles'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import {
  NotExpectedStateAction,
  NotExpectedState,
} from '@/features/onboarding/upload-statements-stage/types'

interface IMissingStatementsProps {
  missingStatements?: MissingStatement[]
  statements?: UserDocument[]
  plaidAccount?: PlaidItem
  isPlaid?: boolean
  notExpectedState?: NotExpectedState
  setNotExpectedValue?: Dispatch<NotExpectedStateAction>
}

interface ImonthYear {
  monthYear: string
  monthYearLabel: string
  accountId: number
}
interface IStatementAccount {
  [name: string]: {
    account: string
    accountId: number
    monthList: ImonthYear[]
  }
}
interface IAccount {
  name: string
  mask: string | null
  logo: string
  financial_institution_name: string | null
  financial_institution_id: string
  financialAccountId: number
}

export const MissingStatements = ({
  missingStatements = [],
  statements = [],
  plaidAccount,
  isPlaid = false,
  notExpectedState = {},
  setNotExpectedValue = (_: NotExpectedStateAction) => {},
}: IMissingStatementsProps) => {
  const [multipleUpload, setMultipleUpload] = useState(false)

  const { statementMonth, statementMonthLabel, accountObject } = useMemo(() => {
    const statementMonth: IStatementAccount = {}
    const statementMonthLabel: string[] = []
    let accountObject: IAccount | undefined
    if (missingStatements.length) {
      const {
        name,
        mask,
        logo,
        financial_institution_name,
        financial_institution_id,
        financialAccountId,
      } = missingStatements[0]
      accountObject = {
        name,
        mask,
        logo,
        financial_institution_name,
        financial_institution_id: financial_institution_id ?? '',
        financialAccountId: financialAccountId ?? 0,
      }
      for (const accountStatements of missingStatements) {
        if (
          accountStatements.statements.length ||
          accountStatements.noStatementExpectedMonths.length
        ) {
          const accountName = accountStatements.name.replace('Plaid ', '')
          statementMonth[accountStatements.name] = {
            account: `${accountStatements.financial_institution_name} ${accountName} ${accountStatements.mask}`,
            accountId: accountStatements.financialAccountId ?? 0,
            monthList: [],
          }
        }
        for (const statement of accountStatements.statements) {
          const monthYear = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR_LONG
          )
          const yearMonth = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH
          )

          const existingMonth = statementMonth[
            accountStatements.name
          ].monthList.find((month) => month.monthYear === yearMonth)

          if (!existingMonth) {
            statementMonth[accountStatements.name].monthList.push({
              monthYear: yearMonth,
              monthYearLabel: monthYear,
              accountId: accountStatements.financialAccountId ?? 0,
            })
          }

          if (!statementMonthLabel.includes(monthYear)) {
            statementMonthLabel.push(monthYear)
          }
        }

        for (const statement of accountStatements.noStatementExpectedMonths) {
          const monthYear = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH
          )
          const monthYearLabel = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR_LONG
          )
          statementMonth[accountStatements.name].monthList.push({
            monthYear,
            monthYearLabel,
            accountId: accountStatements.financialAccountId ?? 0,
          })
          setNotExpectedValue({
            financialAccountId: accountStatements.financialAccountId ?? 0,
            monthYear,
            value: true,
          })

          if (!statementMonthLabel.includes(monthYear)) {
            statementMonthLabel.push(monthYear)
          }
        }
      }
    }
    return {
      statementMonth,
      statementMonthLabel,
      accountObject,
    }
  }, [missingStatements, setNotExpectedValue])

  return !isPlaid ? (
    <div className="flex flex-col gap-4 w-full">
      <Card className="p-6">
        <div className="flex flex-row justify-between items-center w-full pb-6">
          <div className="flex flex-row items-center gap-4">
            <Image
              src={accountObject?.logo ?? ''}
              alt="heard logo"
              className="w-10 h-10"
            />
            <Text variant={TypographyStyles.H2}>
              {accountObject?.financial_institution_name}
            </Text>
          </div>
          {statementMonthLabel.length > 0 && (
            <div className="flex flex-row gap-2">
              <Checkbox
                className="ml-5"
                onClick={() => setMultipleUpload(!multipleUpload)}
                checked={multipleUpload}
              />
              <Text variant={TypographyStyles.LABEL_SM}>
                Upload statement with multiple months/accounts
              </Text>
            </div>
          )}
        </div>

        <Separator />

        {!multipleUpload && (
          <div className="flex flex-col gap-4 w-full">
            {Object.entries(statementMonth).map(
              ([_, { account, monthList }]) => (
                <div key={account} className="flex flex-col gap-2">
                  <div className="flex flex-row  gap-2 items-center">
                    {account.toLowerCase().includes('credit') && (
                      <Icon
                        color="gray"
                        icon={regular('credit-card')}
                        size="2x"
                      />
                    )}
                    {!account.toLowerCase().includes('credit') && (
                      <Icon color="gray" icon={regular('bank')} size="2x" />
                    )}
                    <Text variant={TypographyStyles.BODY_MD}>
                      {String(account)}
                    </Text>
                  </div>
                  {monthList.map(({ accountId, monthYear, monthYearLabel }) => (
                    <MissingStatementCard
                      key={monthYearLabel}
                      month={monthYearLabel}
                      yearMonth={monthYear}
                      financialAccountId={accountId}
                      statements={statements}
                      notExpected={Boolean(
                        notExpectedState[accountId]?.[monthYear]
                      )}
                      setNotExpectedValue={setNotExpectedValue}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {multipleUpload && accountObject && (
          <MissingStatementMultipleUploadCard
            statementMonth={statementMonth}
            accountObject={accountObject}
            statements={statements}
          />
        )}
      </Card>
    </div>
  ) : (
    <Card>
      <div className="flex flex-row items-center gap-4 pb-6">
        {plaidAccount && (
          <InstitutionLogo institution={plaidAccount} className="w-10 h-10" />
        )}
        <Text variant={TypographyStyles.H2}>
          {plaidAccount?.institutionName}
        </Text>
      </div>
      <Text>
        You don&apos;t need to do anything! This financial institution
        doesn&apos;t require statements for setup. We&apos;ll let you know if we
        encounter an issue.
      </Text>
    </Card>
  )
}
